import {
	ICartItem,
	GeneratorCartItem,
	ApiFailedReason,
	ICart,
} from '@quizzach/interfaces';
import { Colors, CtBreak, CtItem, CtList, CtTotal } from '@quizzach/styles';
import {
	formatCartPrice,
	handleQuizType,
	renderDifficulty,
} from '@quizzach/utils';
import React, { useId } from 'react';
import { AlertMessage, CardAuthorMark } from '@quizzach/components';
import { IcClose, QLogo, Xcollab } from '@quizzach/assets';
import { useTranslation } from 'react-i18next';
import i18n from '@quizzach/i18n';

interface SummaryTableProps {
	cart: ICart | undefined;
	error: boolean;
	loading: boolean;
	deleteCartItem: (uuid: string) => Promise<void>;
	deleteInProgress: boolean;
}

const LEVELS = [1, 2, 3];

/** 06·A točke: three fixed dots (easy·medium·hard), colored when a level is
    included, dimmed when excluded so absence is read, not guessed. Color is
    never the only channel - position encodes the level too and the group
    carries the full words for AT/hover. */
const LevelDots = ({ levels }: { levels: number[] }) => {
	const active = LEVELS.filter((level) => levels.includes(level))
		.map((level) => renderDifficulty(level))
		.join(', ');

	return (
		<span className='lvt' role='img' aria-label={active} title={active}>
			{LEVELS.map((level) => (
				<i
					key={level}
					aria-hidden='true'
					className={`lt l${level}${levels.includes(level) ? ' on' : ''}`}
				/>
			))}
		</span>
	);
};

export const SummaryTable = ({
	cart,
	error,
	loading,
	deleteCartItem,
	deleteInProgress,
}: SummaryTableProps) => {
	const id = useId();
	const { t } = useTranslation();

	if (error) {
		return (
			<AlertMessage
				code={ApiFailedReason.cartFetch}
				className='alert'
				type='error'
			/>
		);
	}

	// the empty cart never reaches this table: Cart.tsx returns the dedicated
	// CartEmpty state before rendering it

	if (loading) return <div></div>;

	return (
		<>
			<CtList data-id='cart-summary-list'>
				{cart?.cart_items.map((item: ICartItem) => {
					const isPremade = item.quiz_type === 'premade';
					const authorName = item.premade_quiz?.author?.display_name;
					const showAuthor =
						isPremade && authorName && authorName !== 'quizzach';

					return (
						<CtItem key={`${id}-item-${item.uuid}`}>
							<div className='row'>
								<div className='ttl'>
									<h3>
										{isPremade
											? item.premade_quiz?.title
											: handleQuizType(
													item.quiz_type,
													item.number_of_questions,
													item.premade_quiz?.title,
													item.reserved_questions_count
												)}
									</h3>
									{showAuthor && (
										<span
											className='collab'
											aria-label={i18n.t('cart.quizType.byAuthor', {
												author: authorName,
											})}
										>
											<QLogo size={18} color={Colors.brandDark} />
											<Xcollab size={10} color={Colors.brandDark} />
											<CardAuthorMark displayName={authorName} />
										</span>
									)}
								</div>
								<button
									type='button'
									className={`rm${
										item.quiz_type === 'generator' ? ' gen' : ''
									}`}
									aria-label={t('cart.summary.delete')}
									onClick={() =>
										!deleteInProgress && !loading && deleteCartItem(item.uuid)
									}
								>
									<IcClose size={13} />{' '}
									<span className='rm-lbl'>{t('cart.summary.delete')}</span>
								</button>
								<span
									className={`price${
										item.quiz_type === 'generator' ? ' gen' : ''
									}`}
								>
									{formatCartPrice(item.price)} EUR
								</span>
							</div>

							{item.quiz_type === 'generator' && (
								<CtBreak>
									{item.category_configurations?.map(
										(categoryConfig: GeneratorCartItem) => {
											/* exactly two configurator modes: the user picked
											   the whole category (levels + count live on the
											   category) or picked individual subcategories
											   (levels + count live on each picked one) */
											const wholePick = categoryConfig.question_count !== 0;

											if (wholePick) {
												const levels =
													categoryConfig.subcategory_configurations.find(
														(subcatConfig: any) => subcatConfig.levels?.length
													)?.levels ?? [];

												return (
													<div
														className='br'
														key={`${item.uuid}-cat-${categoryConfig.category.id}`}
													>
														<span className='cat'>
															{categoryConfig.category.name}
														</span>
														{levels.length !== 0 && (
															<LevelDots levels={levels} />
														)}
														<span className='qn'>
															{categoryConfig.question_count}
														</span>
														<span className='unit'>
															{t('cart.summary.questionsUnit', {
																count: categoryConfig.question_count,
															})}
														</span>
													</div>
												);
											}

											/* per-subcategory pick: category header with the
											   muted computed sum, one subrow per picked
											   subcategory */
											const picked =
												categoryConfig.subcategory_configurations.filter(
													(subcatConfig: any) =>
														subcatConfig.question_count !== 0
												);
											const total = picked.reduce(
												(sum: number, subcatConfig: any) =>
													sum + subcatConfig.question_count,
												0
											);

											return (
												<React.Fragment
													key={`${item.uuid}-cat-${categoryConfig.category.id}`}
												>
													<div className='br hd'>
														<span className='cat'>
															{categoryConfig.category.name}
														</span>
														<span className='qn tot'>{total}</span>
														<span className='unit'>
															{t('cart.summary.questionsUnit', {
																count: total,
															})}
														</span>
													</div>
													{picked.map((subcatConfig: any) => (
														<div
															className='br sr'
															key={subcatConfig.subcategory.id}
														>
															<span className='sn'>
																{subcatConfig.subcategory.name}
															</span>
															{subcatConfig.levels &&
																subcatConfig.levels.length !== 0 && (
																	<LevelDots levels={subcatConfig.levels} />
																)}
															<span className='qn'>
																{subcatConfig.question_count}
															</span>
															<span className='unit'>
																{t('cart.summary.questionsUnit', {
																	count: subcatConfig.question_count,
																})}
															</span>
														</div>
													))}
												</React.Fragment>
											);
										}
									)}
								</CtBreak>
							)}
							{item.quiz_type === 'generator' && (
								/* mobile-only strip: generator's delete + price read after
								   the breakdown; desktop keeps the in-row versions (.gen) */
								<div className='below'>
									<button
										type='button'
										className='rm'
										aria-label={t('cart.summary.delete')}
										onClick={() =>
											!deleteInProgress && !loading && deleteCartItem(item.uuid)
										}
									>
										<IcClose size={13} />
									</button>
									<span className='price'>
										{formatCartPrice(item.price)} EUR
									</span>
								</div>
							)}
						</CtItem>
					);
				})}
			</CtList>
			<CtTotal>
				<span className='lbl'>{t('cart.summary.total')}</span>
				<span className='sum'>{formatCartPrice(cart?.total_price)} EUR</span>
			</CtTotal>
		</>
	);
};
